import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Button, Form, Modal, InputGroup, Spinner, Badge } from 'react-bootstrap';
import { 
  FaUsers, FaUser, FaPhoneAlt, FaEnvelope, FaCalendarAlt, 
  FaSearch, FaSyncAlt, FaClipboard, FaCheckCircle, FaTimesCircle, 
  FaEye, FaTimes, FaShieldAlt, FaMapMarkerAlt, FaClock
} from 'react-icons/fa';
import { get } from '../utils/api';
import ProgressDialog from './ProgressDialog';
import { useToast } from './ToastContext';
import NoDataCard from './NoDataCard';
import './AllCustomers.css';

const AllCustomers = () => {
  const { showToast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [sortBy, setSortBy] = useState('name-asc'); // 'name-asc' | 'name-desc' | 'recent'

  // Modal
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await get('/users?role=customer&limit=1000');
      if (response && response.success) {
        setCustomers(response.data || []);
      } else {
        setError(response?.message || 'Failed to fetch customers.');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch customers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleShowModal = (customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
  };

  const safeDate = (d) => {
    if (!d) return 'N/A';
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return 'N/A';
      return dt.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const handleCopy = async (text, label = 'Value') => {
    try {
      if (!navigator?.clipboard) throw new Error('Clipboard not available');
      await navigator.clipboard.writeText(text || '');
      showToast(`${label} copied to clipboard`, 'success');
    } catch (err) {
      showToast(`Failed to copy ${label}`, 'danger');
    }
  };

  // KPIs
  const metrics = useMemo(() => {
    const total = customers.length;
    const active = customers.filter(c => c.is_active !== 0 && c.is_active !== false).length;
    const withMobile = customers.filter(c => Boolean(c.mobile)).length;
    return { total, active, withMobile };
  }, [customers]);

  // Filtered & Sorted
  const filteredCustomers = useMemo(() => {
    return customers
      .filter(c => {
        if (statusFilter === 'active' && (c.is_active === 0 || c.is_active === false)) return false;
        if (statusFilter === 'inactive' && (c.is_active !== 0 && c.is_active !== false)) return false;
        if (searchTerm.trim() !== '') {
          const q = searchTerm.toLowerCase();
          const matchName = (c.name || '').toLowerCase().includes(q);
          const matchEmail = (c.email || '').toLowerCase().includes(q);
          const matchPhone = (c.mobile || '').toLowerCase().includes(q);
          const matchId = String(c.user_id || '').includes(q);
          if (!matchName && !matchEmail && !matchPhone && !matchId) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'name-asc') return (a.name || '').localeCompare(b.name || '');
        if (sortBy === 'name-desc') return (b.name || '').localeCompare(a.name || '');
        if (sortBy === 'recent') return (b.user_id || 0) - (a.user_id || 0);
        return 0;
      });
  }, [customers, statusFilter, searchTerm, sortBy]);

  return (
    <Container fluid className="customers-mgmt-page px-3 px-lg-4 py-3">
      <ProgressDialog show={loading && customers.length === 0} message="Loading customers..." />

      {/* Header & KPI Summary */}
      <div className="cust-header-panel mb-4 p-3 p-lg-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="cust-master-icon">
              <FaUsers />
            </div>
            <div>
              <h3 className="cust-title mb-0">Customer Directory</h3>
              <p className="cust-subtitle mb-0 text-muted">Manage registered retail patients, counter customers and account profiles.</p>
            </div>
          </div>

          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={fetchCustomers} 
            className="cust-refresh-btn d-flex align-items-center gap-2"
          >
            <FaSyncAlt className={loading ? 'fa-spin' : ''} /> Refresh Directory
          </Button>
        </div>

        {/* KPI Metrics */}
        <Row className="g-3">
          <Col xs={6} md={4}>
            <div className="cust-kpi-card">
              <div className="kpi-icon total">
                <FaUsers />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">{metrics.total}</div>
                <div className="kpi-label">Registered Customers</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={4}>
            <div className="cust-kpi-card">
              <div className="kpi-icon active">
                <FaCheckCircle />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-success">{metrics.active}</div>
                <div className="kpi-label">Active Accounts</div>
              </div>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="cust-kpi-card">
              <div className="kpi-icon verified">
                <FaPhoneAlt />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-info">{metrics.withMobile}</div>
                <div className="kpi-label">Mobile Linked</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Toolbar & Search */}
      <div className="cust-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Search Box */}
          <div className="cust-search-wrapper flex-grow-1">
            <InputGroup className="cust-search-group">
              <InputGroup.Text className="bg-transparent border-0 pe-1 text-muted">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by customer name, email, phone number, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="cust-search-input border-0 ps-1"
              />
              {searchTerm && (
                <Button 
                  variant="link" 
                  onClick={() => setSearchTerm('')} 
                  className="cust-clear-search text-muted border-0"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Status Filter Chips */}
          <div className="cust-status-chips d-flex align-items-center gap-2 flex-wrap">
            <button 
              type="button" 
              className={`cust-filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({metrics.total})
            </button>
            <button 
              type="button" 
              className={`cust-filter-chip active-chip ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Active ({metrics.active})
            </button>
            <button 
              type="button" 
              className={`cust-filter-chip inactive ${statusFilter === 'inactive' ? 'active' : ''}`}
              onClick={() => setStatusFilter('inactive')}
            >
              Inactive ({metrics.total - metrics.active})
            </button>
          </div>

          {/* Sort Dropdown */}
          <div className="d-flex align-items-center gap-2">
            <Form.Select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="cust-select-pill"
              size="sm"
            >
              <option value="name-asc">🔤 Name (A - Z)</option>
              <option value="name-desc">🔤 Name (Z - A)</option>
              <option value="recent">📅 Newest Joined</option>
            </Form.Select>
          </div>
        </div>
      </div>

      {/* Customer Cards Grid */}
      {filteredCustomers.length === 0 && !loading ? (
        <div className="cust-no-data-box py-5 text-center rounded glassmorphic-panel">
          <NoDataCard 
            title="No Customers Found"
            message={searchTerm || statusFilter !== 'all' ? "No customers match your search or filter criteria." : "There are no customers registered in the database."}
            icon="👥"
            actionButton={
              (searchTerm || statusFilter !== 'all') ? (
                <Button variant="primary" size="sm" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}>
                  Clear Filters
                </Button>
              ) : null
            }
          />
        </div>
      ) : (
        <div className="cust-cards-grid">
          {filteredCustomers.map((cust) => {
            const initial = (cust.name || 'C').charAt(0).toUpperCase();
            const isActive = cust.is_active !== 0 && cust.is_active !== false;

            return (
              <div className="cust-card-item glassmorphic-panel" key={cust.user_id}>
                <div className="cust-card-header p-3 border-bottom d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <div className="cust-avatar-circle">
                      {initial}
                    </div>
                    <div>
                      <h6 className="cust-card-name mb-0">{cust.name || 'Unnamed Customer'}</h6>
                      <span className="cust-id-pill">ID #{cust.user_id}</span>
                    </div>
                  </div>

                  <span className={`cust-status-badge ${isActive ? 'active' : 'inactive'}`}>
                    {isActive ? 'Active' : 'Disabled'}
                  </span>
                </div>

                <div className="cust-card-body p-3">
                  {/* Phone */}
                  <div className="cust-contact-item d-flex justify-content-between align-items-center mb-2">
                    <span className="cust-contact-label">
                      <FaPhoneAlt className="me-2 text-info small" /> Mobile
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="cust-contact-val text-white">{cust.mobile || 'N/A'}</span>
                      {cust.mobile && (
                        <button 
                          type="button" 
                          className="cust-copy-btn" 
                          onClick={() => handleCopy(cust.mobile, 'Mobile number')}
                          title="Copy mobile number"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="cust-contact-item d-flex justify-content-between align-items-center mb-2">
                    <span className="cust-contact-label">
                      <FaEnvelope className="me-2 text-primary small" /> Email
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="cust-contact-val text-truncate" style={{ maxWidth: '170px' }} title={cust.email || 'N/A'}>
                        {cust.email || 'N/A'}
                      </span>
                      {cust.email && (
                        <button 
                          type="button" 
                          className="cust-copy-btn" 
                          onClick={() => handleCopy(cust.email, 'Email address')}
                          title="Copy email address"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Last Login / Joined */}
                  <div className="cust-meta-row p-2 rounded small mt-2">
                    <div className="d-flex justify-content-between text-muted">
                      <span>Last Seen:</span>
                      <span className="text-white fw-semibold">{safeDate(cust.last_login)}</span>
                    </div>
                  </div>
                </div>

                <div className="cust-card-footer p-2 border-top">
                  <Button 
                    variant="outline-info" 
                    size="sm" 
                    className="w-100 cust-view-btn"
                    onClick={() => handleShowModal(cust)}
                  >
                    <FaEye className="me-1" /> View Full Profile
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Customer Profile Inspector Modal */}
      <Modal show={showModal} onHide={handleCloseModal} scrollable className="cust-modal-modern">
        <Modal.Header closeButton className="cust-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="cust-modal-icon">
              <FaUser />
            </div>
            <div>
              <Modal.Title className="cust-modal-title mb-0">Customer Profile</Modal.Title>
              <div className="cust-modal-subtitle">
                Account <span className="fw-bold text-primary">#{selectedCustomer?.user_id}</span> • Registered Customer
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="cust-modal-body p-4">
          {selectedCustomer && (
            <div>
              <div className="cust-profile-box p-3 rounded mb-4 d-flex align-items-center gap-3">
                <div className="cust-profile-avatar">
                  {(selectedCustomer.name || 'C').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h5 className="cust-profile-name mb-1">{selectedCustomer.name || 'Unnamed'}</h5>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="badge bg-primary">Customer</span>
                    <span className={`cust-status-badge ${selectedCustomer.is_active !== 0 && selectedCustomer.is_active !== false ? 'active' : 'inactive'}`}>
                      {selectedCustomer.is_active !== 0 && selectedCustomer.is_active !== false ? 'Active Account' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="cust-details-section">
                <div className="cust-section-title mb-3">
                  <FaShieldAlt className="me-2 text-info" /> Contact & Account Credentials
                </div>

                <div className="cust-detail-grid">
                  <div className="cust-detail-item p-3 rounded mb-2">
                    <span className="cust-item-label">Email Address</span>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="cust-item-val">{selectedCustomer.email || 'No email registered'}</span>
                      {selectedCustomer.email && (
                        <Button size="sm" variant="outline-light" className="cust-modal-copy-btn" onClick={() => handleCopy(selectedCustomer.email, 'Email')}>
                          <FaClipboard /> Copy
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="cust-detail-item p-3 rounded mb-2">
                    <span className="cust-item-label">Mobile Phone</span>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="cust-item-val">{selectedCustomer.mobile || 'No mobile linked'}</span>
                      {selectedCustomer.mobile && (
                        <Button size="sm" variant="outline-light" className="cust-modal-copy-btn" onClick={() => handleCopy(selectedCustomer.mobile, 'Mobile')}>
                          <FaClipboard /> Copy
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="cust-detail-item p-3 rounded mb-2">
                    <span className="cust-item-label">Last Login Timestamp</span>
                    <span className="cust-item-val">{safeDate(selectedCustomer.last_login)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AllCustomers;
